
import { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import TournamentBracket from './TournamentBracket';

interface ActiveTournamentsProps {
  onRegister: (tournamentName: string) => void;
}

interface Tournament {
  id: string;
  name: string;
  description: string;
  banner_url: string;
  start_date: string;
  end_date: string;
  registration_deadline: string;
  prize_pool: number;
  max_participants: number;
  current_participants: number;
  format: string;
  status: string;
  region: string;
  rules: string;
}

const ActiveTournaments = ({ onRegister }: ActiveTournamentsProps) => {
  const [selectedTournament, setSelectedTournament] = useState<string | null>(null);
  const [bracketTournament, setBracketTournament] = useState<string | null>(null);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);

  const mockTournaments: Tournament[] = [
    {
      id: 'mock-1',
      name: "Brawlhalla Championship Series",
      description: "The ultimate Brawlhalla championship featuring the best players from around the world. Compete in a double-elimination bracket for massive prizes.",
      banner_url: "https://public.readdy.ai/ai/img_res/8e4d457345d0fbba020d44b35726f9f1.jpg",
      start_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      end_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      registration_deadline: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
      prize_pool: 50000,
      max_participants: 128,
      current_participants: 87,
      format: 'double-elimination',
      status: 'upcoming',
      region: 'Global',
      rules: "1v1 format, Best of 3 matches, Standard competitive ruleset, All legends allowed"
    },
    {
      id: 'mock-2',
      name: "Weekly Ranked Showdown",
      description: "Join the most competitive weekly Brawlhalla tournament. Battle through intense matches to climb the ranks and claim victory.",
      banner_url: "https://readdy.ai/api/search-image?query=Brawlhalla%20weekly%20tournament%20banner%20with%20fighting%20game%20characters%2C%20competitive%20gaming%20setup%2C%20esports%20arena%20atmosphere%2C%20red%20and%20black%20color%20scheme%2C%20professional%20tournament%20branding&width=400&height=250&seq=brawl-tournament-2&orientation=landscape",
      start_date: new Date().toISOString(),
      end_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      registration_deadline: new Date().toISOString(),
      prize_pool: 5000,
      max_participants: 64,
      current_participants: 64,
      format: 'single-elimination',
      status: 'active',
      region: 'NA',
      rules: "1v1 format, Best of 5 finals, Draft pick mode"
    },
    {
      id: 'mock-3',
      name: "EU Masters Cup",
      description: "Elite European Brawlhalla competition featuring top-tier players. Prove your tactical prowess and skill.",
      banner_url: "https://readdy.ai/api/search-image?query=Brawlhalla%20European%20tournament%20promotional%20image%20with%20medieval%20warrior%20theme%2C%20competitive%20team%20setup%2C%20orange%20and%20black%20color%20theme%2C%20professional%20esports%20tournament%20design%2C%20gaming%20championship%20branding&width=400&height=250&seq=brawl-tournament-3&orientation=landscape",
      start_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      end_date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
      registration_deadline: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      prize_pool: 15000,
      max_participants: 32,
      current_participants: 24,
      format: 'round-robin',
      status: 'upcoming',
      region: 'EU',
      rules: "1v1 competitive, Best of 3 matches, Round robin into playoffs"
    }
  ];

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tournaments')
        .select('*')
        .in('status', ['upcoming', 'active'])
        .order('start_date', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setTournaments(data);
      } else {
        setTournaments(mockTournaments);
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
      setTournaments(mockTournaments);
    } finally {
      setLoading(false);
    }
  };

  const handleInfoClick = (tournamentId: string) => {
    setSelectedTournament(selectedTournament === tournamentId ? null : tournamentId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-red-600 text-white';
      case 'upcoming':
        return 'bg-green-600 text-white';
      case 'completed':
        return 'bg-gray-600 text-white';
      default:
        return 'bg-yellow-600 text-white';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Live Now';
      case 'upcoming':
        return 'Registration Open';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-red-500 text-4xl mr-3"></i>
            <span className="text-white text-xl">Loading tournaments...</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Active Tournaments</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Join ongoing competitions and prove your skills against the best players in the community
            </p>
          </div>

          {tournaments.length === 0 ? (
            <div className="text-center py-12 bg-gray-900 rounded-xl">
              <i className="ri-trophy-line text-gray-600 text-6xl mb-4"></i>
              <h3 className="text-2xl font-bold text-white mb-2">No Active Tournaments</h3>
              <p className="text-gray-400">Check back soon for upcoming competitions!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tournaments.map((tournament) => (
                <div key={tournament.id} className="bg-gray-900 rounded-xl overflow-hidden border border-gray-700 hover:border-red-500 transition-all duration-300 hover:transform hover:scale-105">
                  <div className="relative">
                    <img 
                      src={tournament.banner_url || `https://readdy.ai/api/search-image?query=Brawlhalla%20esports%20tournament%20banner%20with%20fighting%20game%20aesthetic%2C%20competitive%20gaming%20championship%2C%20red%20and%20black%20theme%2C%20professional%20tournament%20design&width=400&height=250&seq=${tournament.id}&orientation=landscape`}
                      alt={tournament.name}
                      className="w-full h-48 object-cover object-top"
                    />
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(tournament.status)}`}>
                        {getStatusLabel(tournament.status)}
                      </span>
                    </div>
                    {tournament.region && (
                      <div className="absolute top-4 left-4">
                        <span className="bg-black/70 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          {tournament.region}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2">{tournament.name}</h3>
                    <p className="text-red-400 font-semibold mb-4 capitalize">{tournament.format?.replace('-', ' ') || 'Tournament'}</p>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Prize Pool</span>
                        <span className="text-green-400 font-bold">${tournament.prize_pool?.toLocaleString() || '0'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Participants</span>
                        <span className="text-white">{tournament.current_participants || 0}/{tournament.max_participants} players</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Starts</span>
                        <span className="text-white">{tournament.start_date ? new Date(tournament.start_date).toLocaleDateString() : 'TBD'}</span>
                      </div>
                      {tournament.registration_deadline && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Registration Ends</span>
                          <span className="text-yellow-400 text-sm">{new Date(tournament.registration_deadline).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>

                    {/* Progress bar for participants */}
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>Spots Filled</span>
                        <span>{Math.round(((tournament.current_participants || 0) / tournament.max_participants) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-red-600 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(((tournament.current_participants || 0) / tournament.max_participants) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* View Bracket Button */}
                    <button
                      onClick={() => setBracketTournament(tournament.name)}
                      className="w-full mb-4 py-2 px-4 bg-gray-800 hover:bg-gray-700 border border-gray-600 hover:border-red-500 text-white rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-git-merge-line"></i>
                      View Bracket
                    </button>

                    {selectedTournament === tournament.id && (
                      <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-red-500/30">
                        <h4 className="text-white font-semibold mb-2">Tournament Details</h4>
                        <p className="text-gray-300 text-sm mb-3">{tournament.description}</p>
                        {tournament.rules && (
                          <div className="border-t border-gray-700 pt-3">
                            <p className="text-gray-400 text-xs font-semibold mb-1">Rules:</p>
                            <p className="text-gray-300 text-sm">{tournament.rules}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex gap-3">
                      <button 
                        onClick={() => onRegister(tournament.name)}
                        disabled={tournament.status === 'completed' || (tournament.current_participants || 0) >= tournament.max_participants}
                        className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-gamepad-line mr-2"></i>
                        {(tournament.current_participants || 0) >= tournament.max_participants ? 'Full' : 'Join Now'}
                      </button>
                      <button 
                        onClick={() => handleInfoClick(tournament.id)}
                        className={`px-4 py-2 border rounded-lg transition-colors cursor-pointer ${
                          selectedTournament === tournament.id
                            ? 'border-red-500 text-red-400 bg-red-500/10'
                            : 'border-gray-600 text-gray-400 hover:text-white hover:border-red-500'
                        }`}
                        title="View tournament details"
                      >
                        <i className={selectedTournament === tournament.id ? "ri-close-line" : "ri-information-line"}></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Tournament Bracket Modal */}
      {bracketTournament && (
        <TournamentBracket 
          tournamentTitle={bracketTournament}
          onClose={() => setBracketTournament(null)}
        />
      )}
    </>
  );
};

export default ActiveTournaments;
